import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../slices/authSlice";
import { RootState } from "../store/store";
import {
  FiFolder,
  FiGrid,
  FiLogOut,
  FiMail,
  FiUsers,
} from "react-icons/fi";
import BrandLogo from "../components/ui/BrandLogo";
import { useUnreadMessages } from "../hooks/useUnreadMessages";

const navItems = [
  {
    label: "Dashboard",
    href: "/home",
    icon: FiGrid,
    end: true,
    adminOnly: false,
  },
  {
    label: "My applications",
    href: "/applications",
    icon: FiFolder,
    end: false,
    adminOnly: false,
  },
  {
    label: "Messages",
    href: "/messages",
    icon: FiMail,
    end: false,
    adminOnly: false,
  },
  {
    label: "Users",
    href: "/users",
    icon: FiUsers,
    end: false,
    adminOnly: true,
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const unreadMessages = useUnreadMessages();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.isAdmin ?? false;

  const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "";
  const fullName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ")
    : "";
  // The second line of the account block: the role of those who have one, else the address
  const roleLine = user?.isAdmin
    ? "Administrator"
    : user?.canReleaseFilings
    ? "Filing approver"
    : user?.email ?? "";

  function handleLogout() {
    dispatch(logout());
    sessionStorage.clear();
    navigate("/", { replace: true });
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[248px] shrink-0 flex-col gap-7 overflow-y-auto bg-dantzBlue px-4 py-6 transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <button
          onClick={() => {
            onClose();
            navigate("/home");
          }}
          className="block w-full"
        >
          <BrandLogo size="sm" className="w-full" />
        </button>

        <nav aria-label="Main" className="flex flex-1 flex-col gap-1">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex h-11 items-center gap-3 rounded-[10px] px-3 text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-white/[0.12] font-semibold text-white"
                    : "font-medium text-appNav hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    size={18}
                    className={isActive ? "text-dantzOrange" : undefined}
                  />
                  <span className="flex-1">{item.label}</span>
                  {item.href === "/messages" && unreadMessages > 0 && (
                    <span
                      className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-dantzOrange px-1.5 text-xs font-bold text-dantzBlue"
                      aria-label={`${unreadMessages} unread`}
                    >
                      {unreadMessages > 99 ? "99+" : unreadMessages}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {user && (
          <div className="flex items-center gap-3 rounded-xl bg-white/[0.07] p-3">
            <NavLink
              to="/settings"
              onClick={onClose}
              aria-label="Profile"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg"
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-appAvatar text-[13px] font-semibold text-dantzBlue"
              >
                {initials}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-semibold text-white">
                  {fullName}
                </span>
                <span className="truncate text-xs text-appNavMuted">
                  {roleLine}
                </span>
              </span>
            </NavLink>
            <button
              onClick={handleLogout}
              aria-label="Log out"
              className="flex h-11 w-11 flex-none items-center justify-center rounded-[10px] text-appNav transition-colors hover:bg-white/10 hover:text-white"
            >
              <FiLogOut size={18} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
